import { deploymentEnvironment, posthogEnabled, posthogHost, posthogKey } from '@/env'
import Global from '@/Global'
import { scrubUrl } from '@/lib/scrubUrl'
import posthog from 'posthog-js'

let initialized = false

/**
 * True when the address belongs to a staff account (Global.INTERNAL_EMAIL_DOMAINS).
 * Product-usage events from internal users are dropped at the source so PostHog
 * counts customer behaviour only (IMS-306).
 *
 * Finding 3 — the value is trimmed and lower-cased, then matched with a
 * `@${domain}` suffix test. Suffix matching is what makes a multi-`@` value such as
 * `x@y@everactive.com` internal: the real domain is whatever follows the LAST `@`,
 * so a `split('@')[1]` domain lookup let that shape through.
 *
 * Deliberate non-match: a SUBDOMAIN address such as `a@mail.everactive.com` is NOT
 * internal. Only the exact registered domains in INTERNAL_EMAIL_DOMAINS are staff;
 * matching `.everactive.com` as a suffix would also swallow customer-controlled
 * look-alikes, so subdomains must be added to the list explicitly if they are ever
 * used for staff mail.
 *
 * A value with no `@` (a bare domain, an Auth0 username such as `auth0|abc`) or one
 * that is empty/whitespace-only is not internal.
 */
export function isInternalEmail(email?: string | null): boolean {
  if (typeof email !== 'string') {
    return false
  }
  const normalized = email.trim().toLowerCase()
  if (!normalized.includes('@')) {
    return false
  }
  return Global.INTERNAL_EMAIL_DOMAINS.some((domain) => normalized.endsWith(`@${domain}`))
}

// Sanitize URL-shaped properties PostHog auto-collects ($current_url, $referrer,
// $pathname, $initial_*). scrubUrl lives in src/lib because the support-mail
// composable needs the same Auth0 rule and must not import posthog-js to get it.
const URL_PROPS_TO_SCRUB = [
  '$current_url',
  '$referrer',
  '$pathname',
  '$initial_current_url',
  '$initial_referrer',
  '$initial_pathname'
] as const

export function initAnalytics(): void {
  if (initialized || !posthogEnabled || !posthogKey) {
    return
  }
  posthog.init(posthogKey, {
    api_host: posthogHost,
    capture_pageview: 'history_change',
    person_profiles: 'identified_only',
    autocapture: true,
    before_send: (event) => {
      if (!event) {
        return event
      }
      const props = event.properties as Record<string, unknown> | undefined
      if (props) {
        for (const key of URL_PROPS_TO_SCRUB) {
          if (typeof props[key] === 'string') {
            props[key] = scrubUrl(props[key] as string)
          }
        }
      }
      return event
    }
  })
  if (deploymentEnvironment) {
    posthog.register({ environment: deploymentEnvironment })
  }
  initialized = true
}

export type CustomerGroupProperties = {
  name?: string
  status?: string
}

export function useAnalytics() {
  function identify(
    userId: string,
    properties: {
      email?: string
      customerId?: string
      privileges?: string[]
      customer?: CustomerGroupProperties
    } = {}
  ): void {
    if (!initialized) {
      return
    }
    const { customer, ...personProperties } = properties
    posthog.identify(userId, personProperties)
    if (properties.customerId) {
      posthog.group('customer', properties.customerId, customer ?? {})
    }
  }

  function capture(eventName: string, properties: Record<string, unknown> = {}): void {
    if (!initialized) {
      return
    }
    posthog.capture(eventName, properties)
  }

  function reset(): void {
    if (!initialized) {
      return
    }
    posthog.reset()
  }

  return {
    identify,
    capture,
    reset
  }
}
